/**
 * CloseMaintActionModal — Wave 6.3 close-out modal for Account Maintenance
 * actions. Parallel to RAO/CloseCartModal.tsx — same 6-code taxonomy, same
 * matrix structure, same validation contract. Wired to
 * `POST /v3/maint-actions/{id}/resolve`.
 *
 * Origin: maint actions originate from FSR-self triggered flows (CIF Detail
 * kebab) and from RIM/RTM hand-offs. Per the brief, we treat the host as
 * `fsr-self` for the validity matrix; the backend re-validates per its own
 * matrix and may reject inappropriate transitions.
 *
 * Visuals reuse global ModalShell / Button / Pill primitives + design tokens.
 */

import { useEffect, useId, useMemo, useState } from 'react';
import { Button, ModalShell, Pill } from '@/components/primitives';
import type {
  CartResolutionCode,
  MaintActionResolutionDto,
  ResolveMaintActionResponse,
  SystemErrorCode,
} from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { useApi } from '@/modules/RAO/hooks/useApi';
import {
  MIN_REASON_LENGTH,
  getValidResolutionOptions,
} from '@/modules/RAO/utils/cartResolutionMatrix';
import { TestModeBadge } from '@/modules/_shared/TestModeBadge';

export interface CloseMaintActionModalProps {
  open: boolean;
  action: MaintActionResolutionDto;
  onClose: () => void;
  /** Called after a successful resolve so the parent can remove + toast. */
  onResolved: (action: MaintActionResolutionDto, resolutionCode: CartResolutionCode) => void;
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'inline-error'; message: string }
  | { kind: 'fatal-error'; message: string };

export function CloseMaintActionModal({
  open,
  action,
  onClose,
  onResolved,
}: CloseMaintActionModalProps): JSX.Element {
  const { client, makeSignal } = useApi();

  // Maint actions don't have an OriginRole field today — we treat the surface
  // as `fsr-self` for matrix purposes (this is the same context that drives
  // every Maint action launch). Backend re-validates.
  const options = useMemo(
    () => getValidResolutionOptions('fsr-self', action.hasRegulatedArtifact),
    [action.hasRegulatedArtifact],
  );

  const [selectedCode, setSelectedCode] = useState<CartResolutionCode | null>(
    options.length === 1 && options[0] ? options[0].code : null,
  );
  const [reason, setReason] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');
  const [errorCodes, setErrorCodes] = useState<ReadonlyArray<SystemErrorCode>>([]);
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });

  const reasonId = useId();
  const errorCodeId = useId();

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    client.retail.config.systemErrorCodes({ signal: makeSignal() }).then(
      (res) => {
        if (cancelled) return;
        const anyRes = res as unknown as { success?: boolean; data?: SystemErrorCode[] };
        if (anyRes.success !== false) setErrorCodes(anyRes.data ?? []);
      },
      () => {
        // Non-fatal — see CloseCartModal for the same pattern.
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSelectedCode(options.length === 1 && options[0] ? options[0].code : null);
    setReason('');
    setErrorCode('');
    setSubmitState({ kind: 'idle' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action.maintActionId]);

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= MIN_REASON_LENGTH;
  const errorCodeValid = selectedCode !== 'System-Error' || errorCode.length > 0;
  const canSubmit =
    selectedCode !== null
    && reasonValid
    && errorCodeValid
    && submitState.kind !== 'submitting';

  async function handleSubmit(): Promise<void> {
    if (!selectedCode || !reasonValid) return;
    if (selectedCode === 'System-Error' && !errorCode) return;
    setSubmitState({ kind: 'submitting' });
    try {
      const body: Parameters<typeof client.retail.maintActions.resolve>[1] = {
        resolutionCode: selectedCode,
        resolutionReason: trimmedReason,
      };
      if (selectedCode === 'System-Error') body.errorCode = errorCode;

      const res: ResolveMaintActionResponse = await client.retail.maintActions.resolve(
        action.maintActionId,
        body,
        { signal: makeSignal() },
      );
      if (!res.success) {
        setSubmitState({
          kind: 'fatal-error',
          message: res.error ?? 'Unable to close the maintenance action.',
        });
        return;
      }
      const resolved: MaintActionResolutionDto = res.maintAction ?? {
        ...action,
        resolutionCode: selectedCode,
        resolutionReason: trimmedReason,
        errorCode: selectedCode === 'System-Error' ? errorCode : undefined,
      };
      onResolved(resolved, selectedCode);
      onClose();
    } catch (err) {
      if (ApiError.isApiError(err)) {
        setSubmitState({ kind: 'fatal-error', message: err.message });
        return;
      }
      if (err instanceof Error && err.name === 'AbortError') return;
      setSubmitState({
        kind: 'fatal-error',
        message: err instanceof Error ? err.message : 'Unexpected error.',
      });
    }
  }

  const noOptions = options.length === 0;

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Close maintenance action"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!canSubmit || noOptions}
          >
            {submitState.kind === 'submitting' ? 'Closing…' : 'Close action'}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5 }}>
        Closing <strong>{action.displayLabel}</strong>
        {action.customerName && (
          <>
            {' '}
            for <strong>{action.customerName}</strong>
          </>
        )}
        {action.isTestMode && (
          <>
            {' '}
            <TestModeBadge active size="sm" />
          </>
        )}
        . Choose the resolution that matches the outcome of this action.
      </div>

      {noOptions && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
          }}
        >
          No resolution codes are valid for this action right now.
        </div>
      )}

      {!noOptions && (
        <div
          role="radiogroup"
          aria-label="Resolution code"
          style={{ display: 'grid', gap: 'var(--sp-4)' }}
        >
          {options.map((opt) => {
            const checked = selectedCode === opt.code;
            return (
              <label
                key={opt.code}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 'var(--sp-4)',
                  padding: 'var(--sp-5)',
                  border: `1px solid ${checked ? 'var(--pcb-blue)' : 'var(--surface-border)'}`,
                  borderRadius: 'var(--r-md)',
                  background: checked ? 'var(--info-bg)' : 'var(--surface-card)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="radio"
                  name="maint-resolution-code"
                  value={opt.code}
                  checked={checked}
                  onChange={() => setSelectedCode(opt.code)}
                  style={{ marginTop: 3 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: 'var(--ink-900)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--sp-3)',
                    }}
                  >
                    {opt.label}
                    {opt.tone === 'danger' && <Pill variant="err">Compliance review</Pill>}
                    {opt.tone === 'positive' && <Pill variant="ok">Terminal success</Pill>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>{opt.description}</div>
                </div>
              </label>
            );
          })}
        </div>
      )}

      {selectedCode === 'System-Error' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <label
            htmlFor={errorCodeId}
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              color: 'var(--ink-500)',
            }}
          >
            System error code <span style={{ color: 'var(--pcb-red)' }}>*</span>
          </label>
          <select
            id={errorCodeId}
            value={errorCode}
            onChange={(e) => setErrorCode(e.target.value)}
            style={{
              width: '100%',
              padding: 'var(--sp-3) var(--sp-4)',
              border: '1px solid var(--ink-300)',
              borderRadius: 'var(--r-md)',
              fontSize: 13,
            }}
          >
            <option value="">Select the failing system / component...</option>
            {errorCodes
              .filter((ec) => ec.isActive)
              .map((ec) => (
                <option key={ec.code} value={ec.code}>
                  {ec.label}
                  {ec.sourceSystem ? ` — ${ec.sourceSystem}` : ''}
                </option>
              ))}
          </select>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <label
          htmlFor={reasonId}
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            color: 'var(--ink-500)',
          }}
        >
          Reason <span style={{ color: 'var(--pcb-red)' }}>*</span>
        </label>
        <textarea
          id={reasonId}
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            selectedCode === 'Adverse-Action'
              ? 'E.g. AA-02 Insufficient Credit References'
              : 'Document the reason this maintenance action is being closed.'
          }
          style={{
            width: '100%',
            padding: 'var(--sp-4)',
            border: '1px solid var(--ink-300)',
            borderRadius: 'var(--r-md)',
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            resize: 'vertical',
          }}
        />
        <div style={{ fontSize: 11, color: reasonValid ? 'var(--ink-500)' : 'var(--pcb-red)' }}>
          {reasonValid
            ? `${trimmedReason.length} characters`
            : `At least ${MIN_REASON_LENGTH} characters required (${trimmedReason.length} so far).`}
        </div>
      </div>

      {submitState.kind === 'fatal-error' && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            fontSize: 13,
          }}
        >
          {submitState.message}
        </div>
      )}
    </ModalShell>
  );
}
